import React, { useEffect, useState } from 'react';
import {
  CalendarClock,
  Check,
  Edit3,
  Loader2,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  createObligation,
  deleteObligation,
  listObligations,
  updateObligation,
} from './contractDetailApi';
import { formatDate } from './contractDetailUtils';

const KIND_OPTIONS = [
  { value: 'claim_window', label: 'Claim window', tone: 'blue' },
  { value: 'cure_period', label: 'Cure period', tone: 'yellow' },
  { value: 'breach_notice', label: 'Breach notice', tone: 'red' },
  { value: 'price_increase', label: 'Price increase', tone: 'purple' },
  { value: 'other', label: 'Other', tone: 'gray' },
];

const kindMeta = (kind) => KIND_OPTIONS.find((k) => k.value === kind) || KIND_OPTIONS[4];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${String(dateStr).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const base = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return Math.round((target.getTime() - base.getTime()) / (24 * 60 * 60 * 1000));
}

// Mirrors the Term bar / ExpirationBar palette: red ≤ 14d or overdue,
// yellow ≤ 60d, gray beyond.
function dueChip(days) {
  if (days === null) return null;
  if (days < 0) return { cls: 'chip sq red', text: `${Math.abs(days)}d overdue` };
  if (days === 0) return { cls: 'chip sq red', text: 'due today' };
  if (days <= 14) return { cls: 'chip sq red', text: `${days}d left` };
  if (days <= 60) return { cls: 'chip sq yellow', text: `${days}d left` };
  return { cls: 'chip sq gray', text: `${days}d left` };
}

const emptyDraft = { kind: 'other', title: '', dueDate: '', clauseRef: '', description: '' };

function ObligationEditor({ draft, setDraft, saving, onSave, onCancel }) {
  return (
    <div style={{ padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8 }}>
        <input
          className="input"
          autoFocus
          placeholder="e.g. SLA service-credit claim window — 30 days"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <select className="select" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
          {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8 }}>
        <input
          className="input"
          type="date"
          value={draft.dueDate || ''}
          onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
          title="Due date (leave empty if the contract only defines a relative window)"
        />
        <input
          className="input"
          placeholder="Clause ref (e.g. § 9.3) — optional"
          value={draft.clauseRef || ''}
          onChange={(e) => setDraft({ ...draft, clauseRef: e.target.value })}
        />
      </div>
      <textarea
        className="textarea"
        rows={2}
        placeholder="Trigger, window length, and consequence of missing it — optional"
        value={draft.description || ''}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        style={{ resize: 'vertical', minHeight: 40, lineHeight: 1.5 }}
      />
      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" className="btn ghost xs" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="button" className="btn primary xs" onClick={onSave} disabled={saving || !draft.title.trim()}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>
    </div>
  );
}

// Docketed obligations (doc-70): typed dated deadlines (claim windows, cure
// periods, breach notices, price-increase windows) the reminder engine
// watches. AI-seeded on upload/Regenerate; legal edits, adds, dismisses.
export default function ObligationsCard({ contractId, canWrite, regenerating = false }) {
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null); // row id, or 'new'
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (regenerating) return; // refetch when the AI pass finishes
    let cancelled = false;
    listObligations(contractId)
      .then((rows) => { if (!cancelled) { setItems(rows); setError(null); } })
      .catch((err) => { if (!cancelled) { setItems([]); setError(err.message); } });
    return () => { cancelled = true; };
  }, [contractId, regenerating]);

  const cancelEdit = () => { setEditingId(null); setDraft(emptyDraft); };

  const commitEdit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      kind: draft.kind,
      title: draft.title.trim(),
      dueDate: draft.dueDate || null,
      clauseRef: draft.clauseRef || null,
      description: draft.description || null,
    };
    try {
      if (editingId === 'new') {
        const created = await createObligation(contractId, payload);
        setItems((prev) => [...(prev || []), created]);
      } else {
        const updated = await updateObligation(contractId, editingId, payload);
        setItems((prev) => (prev || []).map((o) => (o.id === updated.id ? updated : o)));
      }
      cancelEdit();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (row, status) => {
    setBusyId(row.id);
    setError(null);
    try {
      const updated = await updateObligation(contractId, row.id, { status });
      setItems((prev) => (prev || []).map((o) => (o.id === updated.id ? updated : o)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    setBusyId(row.id);
    setError(null);
    try {
      await deleteObligation(contractId, row.id);
      setItems((prev) => (prev || []).filter((o) => o.id !== row.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const list = items || [];
  const openCount = list.filter((o) => o.status === 'open').length;

  return (
    <div className="card">
      <div className="card-header compact">
        <div className="row" style={{ gap: 8 }}>
          <CalendarClock className="w-[13px] h-[13px]" style={{ color: 'var(--text-blue)' }} />
          <span className="card-title">Obligations</span>
          {openCount > 0 && <span className="chip sq blue">{openCount} open</span>}
        </div>
      </div>

      <div className="card-body flush">
        {error && (
          <div style={{ padding: '10px 18px', fontSize: 12.5, color: 'var(--text-critical)' }}>{error}</div>
        )}
        {items === null && !error && (
          <div className="muted" style={{ padding: '16px 18px', fontSize: 12.5 }}>Loading…</div>
        )}
        {items !== null && list.length === 0 && editingId === null && (
          <div className="muted" style={{ padding: '16px 18px', fontSize: 12.5 }}>
            No docketed obligations. AI extracts dated obligations (claim windows, cure periods, breach notices, price-increase windows) on upload and Regenerate; open ones with a due date get Slack reminders.
          </div>
        )}
        {list.map((o) => {
          if (editingId === o.id) {
            return (
              <ObligationEditor
                key={o.id}
                draft={draft}
                setDraft={setDraft}
                saving={saving}
                onSave={commitEdit}
                onCancel={cancelEdit}
              />
            );
          }
          const meta = kindMeta(o.kind);
          const inactive = o.status !== 'open';
          const chip = inactive ? null : dueChip(daysUntil(o.dueDate));
          const busy = busyId === o.id;
          return (
            <div
              key={o.id}
              style={{
                padding: '10px 18px',
                borderBottom: '0.5px solid var(--border-primary)',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                alignItems: 'flex-start',
                opacity: inactive ? 0.55 : 1,
              }}
            >
              <div>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <span className={`chip sq ${meta.tone}`}>{meta.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 450, textDecoration: inactive && o.status === 'done' ? 'line-through' : 'none' }}>
                    {o.title}
                  </span>
                  {o.source === 'ai' && (
                    <span className="chip sq purple" title="Extracted by AI"><Sparkles className="w-2.5 h-2.5" /> AI</span>
                  )}
                  {inactive && <span className="chip sq gray">{o.status}</span>}
                </div>
                <div className="row muted" style={{ gap: 10, marginTop: 4, fontSize: 12, flexWrap: 'wrap' }}>
                  {o.dueDate ? (
                    <span className="mono">Due {formatDate(o.dueDate)}</span>
                  ) : (
                    <span>No fixed date</span>
                  )}
                  {chip && <span className={chip.cls}>{chip.text}</span>}
                  {o.clauseRef && <span className="mono">{o.clauseRef}</span>}
                </div>
                {o.description && (
                  <div style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 4, color: 'var(--text-secondary)' }}>
                    {o.description}
                  </div>
                )}
              </div>
              {canWrite && (
                <div className="row" style={{ gap: 2 }}>
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ margin: 4 }} />
                  ) : o.status === 'open' ? (
                    <>
                      <button type="button" className="icon-btn xs" title="Edit" aria-label="Edit"
                        onClick={() => {
                          setEditingId(o.id);
                          setDraft({
                            kind: o.kind,
                            title: o.title,
                            dueDate: o.dueDate || '',
                            clauseRef: o.clauseRef || '',
                            description: o.description || '',
                          });
                        }}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" className="icon-btn xs" title="Mark done" aria-label="Mark done"
                        onClick={() => setStatus(o, 'done')} style={{ color: 'var(--text-green)' }}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" className="icon-btn xs" title="Dismiss (stop reminders)" aria-label="Dismiss"
                        onClick={() => setStatus(o, 'dismissed')}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="icon-btn xs" title="Reopen" aria-label="Reopen"
                        onClick={() => setStatus(o, 'open')}>
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" className="icon-btn xs" title="Delete" aria-label="Delete"
                        onClick={() => remove(o)} style={{ color: 'var(--text-critical)' }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {editingId === 'new' && (
          <ObligationEditor
            draft={draft}
            setDraft={setDraft}
            saving={saving}
            onSave={commitEdit}
            onCancel={cancelEdit}
          />
        )}
        {canWrite && editingId === null && (
          <div style={{ padding: '8px 18px' }}>
            <button type="button" className="btn ghost sm" onClick={() => { setEditingId('new'); setDraft(emptyDraft); }}>
              <Plus className="w-3.5 h-3.5" />
              Add obligation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
